import * as React from 'react';

interface Props {
  children: React.ReactNode;
  initialStudentId?: number;
  initialQuestionId?: number;
}

export type SetStudentQuestion = ({
  studentId,
  questionId,
}: {
  studentId: number;
  questionId: number;
}) => void;

const StudentIdContext = React.createContext<number>(null);
const QuestionIdContext = React.createContext<number>(null);
const SelectStudentQuestion = React.createContext<SetStudentQuestion>(() => {});

export function StudentScoreProvider({
  children,
  initialQuestionId = null,
  initialStudentId = null,
}: Props) {
  const [studentId, setStudentId] = React.useState<number>(initialStudentId);
  const [questionId, setQuestionId] = React.useState<number>(initialQuestionId);

  const selectStudentQuestion = React.useCallback(
    ({ studentId, questionId }: { studentId: number; questionId: number }) => {
      setStudentId(studentId);
      setQuestionId(questionId);
    },
    [setStudentId, setQuestionId]
  );

  return (
    <StudentIdContext.Provider value={studentId}>
      <QuestionIdContext.Provider value={questionId}>
        <SelectStudentQuestion.Provider value={selectStudentQuestion}>
          {children}
        </SelectStudentQuestion.Provider>
      </QuestionIdContext.Provider>
    </StudentIdContext.Provider>
  );
}

function useStudentId() {
  return React.useContext(StudentIdContext);
}

function useQuestionId() {
  return React.useContext(QuestionIdContext);
}

function useSelectStudentQuestion() {
  return React.useContext(SelectStudentQuestion);
}

export function useStudentScoreProvider() {
  return {
    studentId: useStudentId(),
    questionId: useQuestionId(),
    selectStudentQuestion: useSelectStudentQuestion(),
  };
}
